import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { toast } from 'sonner';
import { printCard } from '../lib/receipt';
import { cardLabels, CARD_LANGUAGES } from '../lib/cardLabels';
import { fmtEur } from '../lib/constants';
import { QRCodeCanvas } from 'qrcode.react';
import { PageHeader } from '../components/common/PageHeader';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AmountInput } from '../components/common/NumericKeypad';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Gift, Plus, Search, Trash2, Printer, Wallet, Ban, CheckCircle2, CreditCard, Lock, ScrollText,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSecurityPin } from '../context/SecurityPinContext';
import { AuditLogDialog } from '../components/common/AuditLogDialog';

const PRESETS = [10, 25, 50, 100];
const publicCardUrl = (code) => `${window.location.origin}/carta/regalo/${code}`;

const statusBadge = (status) => {
  if (status === 'disabled') return { key: 'giftCards.stDisabled', cls: 'bg-destructive/10 text-destructive border-destructive/20' };
  if (status === 'depleted') return { key: 'giftCards.stDepleted', cls: 'bg-muted text-muted-foreground border-border' };
  return { key: 'giftCards.stActive', cls: 'bg-pos-free/10 text-pos-free border-pos-free/20' };
};

export default function GiftCardsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';
  const { runSensitive } = useSecurityPin();
  const [auditOpen, setAuditOpen] = useState(false);

  const [cards, setCards] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ amount: '', recipient_name: '', message: '', language: 'es' });
  const [saving, setSaving] = useState(false);

  const [selected, setSelected] = useState(null);
  const [printLang, setPrintLang] = useState('es');
  const [topup, setTopup] = useState('');
  const [printing, setPrinting] = useState(false);
  const qrRef = useRef(null);

  const loadCards = useCallback(async (search) => {
    const { data } = await api.get('/giftcards', { params: search ? { q: search } : {} });
    setCards(data || []);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try { await loadCards(''); }
    catch (e) { toast.error(t('giftCards.loadError')); }
    finally { setLoading(false); }
  }, [loadCards, t]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    const id = setTimeout(() => { loadCards(q).catch(() => {}); }, 300);
    return () => clearTimeout(id);
  }, [q, loadCards]);

  const totalBalance = cards.reduce((s, c) => s + Number(c.balance || 0), 0);

  const createCard = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error(t('giftCards.errAmount')); return; }
    setSaving(true);
    try {
      const { data } = await runSensitive((cfg) => api.post('/giftcards', {
        amount: amt, recipient_name: form.recipient_name, message: form.message, language: form.language,
      }, cfg));
      toast.success(t('giftCards.created'));
      setNewOpen(false);
      setForm({ amount: '', recipient_name: '', message: '', language: 'es' });
      await loadCards(q);
      openCard(data.id);
    } catch (e) {
      if (e?.message === 'cancelled') { setSaving(false); return; }
      toast.error(e?.response?.data?.detail || t('giftCards.createError'));
    } finally { setSaving(false); }
  };

  const openCard = async (id) => {
    try {
      const { data } = await api.get(`/giftcards/${id}`);
      setSelected(data);
      setPrintLang(data.language || 'es');
      setTopup('');
    } catch (e) { toast.error(t('giftCards.notFound')); }
  };

  const refreshSelected = (data) => {
    setSelected(data);
    setCards((prev) => prev.map((c) => (c.id === data.id ? { ...c, balance: data.balance, status: data.status } : c)));
  };

  const doTopup = async () => {
    const amt = parseFloat(topup);
    if (!amt || amt <= 0) { toast.error(t('giftCards.errTopup')); return; }
    try {
      const { data } = await runSensitive((cfg) => api.post(`/giftcards/${selected.id}/topup`, { amount: amt }, cfg));
      refreshSelected(data);
      setTopup('');
      toast.success(t('giftCards.toppedUp'));
    } catch (e) { if (e?.message !== 'cancelled') toast.error(e?.response?.data?.detail || t('common.error')); }
  };

  const doDisable = async () => {
    try {
      const { data } = await runSensitive((cfg) => api.post(`/giftcards/${selected.id}/disable`, {}, cfg));
      refreshSelected(data);
      toast.success(t('giftCards.disabled'));
    } catch (e) { if (e?.message !== 'cancelled') toast.error(e?.response?.data?.detail || t('common.error')); }
  };

  const deleteCard = async () => {
    if (!window.confirm(t('giftCards.deleteConfirm'))) return;
    try {
      await runSensitive((cfg) => api.delete(`/giftcards/${selected.id}`, cfg));
      toast.success(t('giftCards.deleted'));
      setSelected(null);
      await loadCards(q);
    } catch (e) { if (e?.message !== 'cancelled') toast.error(e?.response?.data?.detail || t('common.error')); }
  };

  // Stampa il buono su scontrino verticale con saldo reale aggiornato.
  const printGiftCard = async () => {
    if (!selected) return;
    setPrinting(true);
    try {
      const { data } = await api.get(`/public/giftcard/${selected.code}`);
      const lab = cardLabels(printLang);
      const url = publicCardUrl(selected.code);
      let qrDataUrl = null;
      try { qrDataUrl = qrRef.current ? qrRef.current.toDataURL('image/png') : null; } catch { /* */ }
      const lines = [];
      if (data?.card?.recipient_name) lines.push(`${lab.forPrefix}: ${data.card.recipient_name}`);
      if (data?.card?.message) lines.push(data.card.message);
      await printCard({
        businessName: data?.business?.name || '',
        logo: data?.business?.logo || null,
        title: lab.giftTitle,
        lines,
        valueLabel: lab.balance,
        valueText: fmtEur(data?.card?.balance ?? selected.balance),
        code: selected.code,
        qrUrl: url,
        qrHint: lab.giftHint,
        footer: data?.business?.name || '',
      }, { qrDataUrl });
      toast.success(t('giftCards.sentToPrinter'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('giftCards.printError'));
    } finally { setPrinting(false); }
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl" data-testid="giftcards-page">
      <PageHeader title={t('giftCards.title')} subtitle={t('giftCards.subtitle')}>
        {isAdminOrManager ? (
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setAuditOpen(true)} data-testid="giftcard-audit-button">
              <ScrollText className="h-4 w-4 mr-2" /> {t('giftCards.register')}
            </Button>
            <Button className="rounded-xl" onClick={() => setNewOpen(true)} data-testid="giftcard-new-button">
              <Plus className="h-4 w-4 mr-2" /> {t('giftCards.newCard')}
            </Button>
          </div>
        ) : (
          <Badge variant="outline" className="text-xs bg-muted text-muted-foreground" data-testid="giftcard-readonly-badge">
            <Lock className="h-3 w-3 mr-1" /> {t('giftCards.readOnly')}
          </Badge>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Badge variant="secondary" className="rounded-lg text-sm py-1.5 px-3" data-testid="giftcard-count-badge">
          <Gift className="h-4 w-4 mr-1.5 text-primary" /> {t('giftCards.countBadge', { count: cards.length })}
        </Badge>
        <Badge variant="secondary" className="rounded-lg text-sm py-1.5 px-3" data-testid="giftcard-total-badge">
          <Wallet className="h-4 w-4 mr-1.5 text-primary" /> {t('giftCards.totalBadge', { total: fmtEur(totalBalance) })}
        </Badge>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-10" placeholder={t('giftCards.searchPlaceholder')} value={q} onChange={(e) => setQ(e.target.value)} data-testid="giftcard-search-input" />
      </div>

      {loading ? (
        <div className="text-muted-foreground p-6">{t('common.loading')}</div>
      ) : cards.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="giftcard-empty">
          <Gift className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <div className="font-medium">{t('giftCards.emptyTitle')}</div>
          <div className="text-sm">{t('giftCards.emptyDesc')}</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {cards.map((c) => {
            const sb = statusBadge(c.status);
            return (
              <button
                key={c.id}
                onClick={() => openCard(c.id)}
                className="text-left rounded-2xl border border-border bg-card p-4 transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid={`giftcard-item-${c.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono font-semibold tracking-wider truncate">{c.code}</div>
                    {c.recipient_name ? <div className="text-xs text-muted-foreground mt-0.5 truncate">{c.recipient_name}</div> : null}
                    <Badge variant="outline" className={`mt-2 text-[10px] ${sb.cls}`}>{t(sb.key)}</Badge>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-2xl font-bold text-primary leading-none tabular">{fmtEur(c.balance)}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">{t('giftCards.balance')}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Dialog: nuovo buono */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="sm:max-w-md" data-testid="giftcard-new-dialog">
          <DialogHeader><DialogTitle>{t('giftCards.newCardTitle')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('giftCards.amount')}</Label>
              <div className="grid grid-cols-4 gap-2 mt-1.5">
                {PRESETS.map((p) => (
                  <Button
                    key={p}
                    type="button"
                    variant={String(form.amount) === String(p) ? 'default' : 'outline'}
                    className="rounded-xl"
                    onClick={() => setForm({ ...form, amount: String(p) })}
                    data-testid={`giftcard-preset-${p}`}
                  >€{p}</Button>
                ))}
              </div>
              <AmountInput
                value={form.amount}
                onValueChange={(v) => setForm({ ...form, amount: v })}
                label={t('giftCards.cardAmount')}
                className="mt-2"
                placeholder={t('giftCards.customAmount')}
                data-testid="giftcard-form-amount"
              />
            </div>
            <div>
              <Label>{t('giftCards.recipientOptional')}</Label>
              <Input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} placeholder="Es. Mario Rossi" data-testid="giftcard-form-recipient" />
            </div>
            <div>
              <Label>{t('giftCards.messageOptional')}</Label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t('giftCards.messagePlaceholder')} rows={2} data-testid="giftcard-form-message" />
            </div>
            <div>
              <Label>{t('giftCards.receiptLanguage')}</Label>
              <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                <SelectTrigger className="mt-1" data-testid="giftcard-form-language"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CARD_LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">{t('giftCards.receiptLanguageHint')}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setNewOpen(false)}>{t('common.cancel')}</Button>
            <Button className="rounded-xl" onClick={createCard} disabled={saving} data-testid="giftcard-form-save">{t('giftCards.createCard')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: dettaglio buono */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="giftcard-detail-dialog">
          {selected && (
            <>
              <DialogHeader><DialogTitle>{t('giftCards.detailTitle')}</DialogTitle></DialogHeader>
              <ScrollArea className="max-h-[70vh] pr-3">
                <div className="space-y-4">
                  <div className="flex items-center gap-4 rounded-2xl bg-muted/50 p-4">
                    <div className="bg-white p-2 rounded-xl shrink-0">
                      <QRCodeCanvas ref={qrRef} value={publicCardUrl(selected.code)} size={104} includeMargin data-testid="giftcard-qr" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-muted-foreground">{t('giftCards.cardCode')}</div>
                      <div className="font-mono font-semibold tracking-wider">{selected.code}</div>
                      <div className="mt-2 text-3xl font-bold text-primary leading-none tabular" data-testid="giftcard-balance">{fmtEur(selected.balance)}</div>
                      {selected.recipient_name ? <div className="text-xs text-muted-foreground mt-1">{t('giftCards.forPrefix')}: {selected.recipient_name}</div> : null}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Select value={printLang} onValueChange={setPrintLang}>
                      <SelectTrigger className="w-36 shrink-0" data-testid="giftcard-print-language"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CARD_LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" className="flex-1 rounded-xl" onClick={printGiftCard} disabled={printing} data-testid="giftcard-print-button">
                      <Printer className="h-4 w-4 mr-2" /> {printing ? t('giftCards.printing') : t('giftCards.printCard')}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground -mt-2">{t('giftCards.printHint')}</p>

                  {isAdminOrManager && selected.status !== 'disabled' && (
                    <div>
                      <Label>{t('giftCards.topup')}</Label>
                      <div className="flex gap-2 mt-1">
                        <AmountInput value={topup} onValueChange={setTopup} label={t('giftCards.topup')} placeholder={t('giftCards.amountEur')} data-testid="giftcard-topup-amount" />
                        <Button className="rounded-xl shrink-0" onClick={doTopup} data-testid="giftcard-topup-button"><Plus className="h-4 w-4 mr-1" /> {t('giftCards.topupBtn')}</Button>
                      </div>
                    </div>
                  )}

                  {selected.history && selected.history.length > 0 && (
                    <div>
                      <Label className="mb-2 block">{t('giftCards.history')}</Label>
                      <div className="space-y-1.5">
                        {selected.history.slice(0, 12).map((h, i) => (
                          <div key={i} className="flex items-center justify-between text-sm border-b border-border/60 pb-1.5">
                            <span className="text-muted-foreground truncate mr-2">{h.note || h.type}</span>
                            <span className={`font-semibold shrink-0 tabular ${h.amount >= 0 ? 'text-pos-free' : 'text-destructive'}`}>{h.amount >= 0 ? '+' : ''}{fmtEur(h.amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
              <DialogFooter className="flex-row justify-between sm:justify-between">
                <div className="flex gap-1">
                  {isAdminOrManager && selected.status !== 'disabled' && (
                    <Button variant="ghost" className="rounded-xl text-muted-foreground" onClick={doDisable} data-testid="giftcard-disable-button">
                      <Ban className="h-4 w-4 mr-1" /> {t('giftCards.disable')}
                    </Button>
                  )}
                  {isAdminOrManager && (
                    <Button variant="ghost" className="rounded-xl text-destructive hover:text-destructive" onClick={deleteCard} data-testid="giftcard-delete-button">
                      <Trash2 className="h-4 w-4 mr-1" /> {t('common.delete')}
                    </Button>
                  )}
                </div>
                <Button variant="outline" className="rounded-xl" onClick={() => setSelected(null)}>{t('common.close')}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AuditLogDialog open={auditOpen} onOpenChange={setAuditOpen} entityType="giftcard" title={t('giftCards.auditTitle')} />
    </div>
  );
}
